import { createApiCall } from "services/api";

import { transformMfaChallenge, transformToRequestRegister } from "./transformations";
import { RegistrationFormValues, LoginFormValues, LoginResponse, AuthTokens, MfaChallenge } from "./types";
import { transformErrorResponse } from "services/api/transformation";

/**
 * Prijava lozinkom. Račun s uključenom dvostrukom provjerom umjesto tokena vraća izazov
 * (`mfaChallenge`); prijava se tada dovršava kodom iz e-pošte kroz `verifyMfaCode`.
 */
export const logIn = async (
  formValues: LoginFormValues
): Promise<LoginResponse> => {

  const resp = await createApiCall(
    {
      path: "/v1/users/authenticate",
      method: "POST",
      body: {
        username: formValues.email,
        password: formValues.password,
      },
    },
    {
      getMessage: {
        // Dok se čeka kod, korisnik još nije prijavljen — poruka dobrodošlice bi lagala.
        success: (resp: any) => (resp?.data?.mfaRequired ? "" : "Uspješno ste se prijavili! Dobro došli!"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return {
    refreshToken: resp.data.refreshToken ?? "",
    accessToken: resp.data.accessToken ?? "",
    mfaChallenge: resp.data.mfaRequired ? transformMfaChallenge(resp.data) : null,
  };
};

/** Drugi korak prijave: kod iz e-pošte uz oznaku izazova. Ispravan kod vraća tokene. */
export const verifyMfaCode = async (
  challengeId: string,
  code: string
): Promise<AuthTokens> => {
  const resp = await createApiCall(
    {
      path: "/v1/users/authenticate/mfa",
      method: "POST",
      body: { challengeId, code },
    },
    {
      getMessage: {
        success: () => "Uspješno ste se prijavili! Dobro došli!",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return {
    refreshToken: resp.data.refreshToken,
    accessToken: resp.data.accessToken,
  };
};

/** Novi kod za isti izazov. Poslužitelj ga dopušta najranije minutu nakon prethodnog. */
export const resendMfaCode = async (challengeId: string): Promise<MfaChallenge> => {
  const resp = await createApiCall(
    {
      path: "/v1/users/authenticate/mfa/resend",
      method: "POST",
      body: { challengeId },
    },
    {
      getMessage: {
        success: () => "Novi kod je poslan na vašu e-mail adresu.",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformMfaChallenge(resp.data);
};

/**
 * Odjava na poslužitelju: poništava refresh tokene.
 *
 * Brisanje iz preglednika nije dovoljno — token u bazi ostaje valjan do isteka, pa izlazak iz
 * preuzete sesije zapravo ništa ne bi zatvorio.
 */
export const signOutOnServer = async (): Promise<void> => {
  await createApiCall({ path: "/v1/users/sign-out", method: "POST" })();
};

/**
 * Preuzimanje računa: naše osoblje iz admina otvara ovu aplikaciju kao odabrani korisnik.
 *
 * Iz adresne trake dolazi jednokratni kod, ne token — token bi ondje ostao u povijesti
 * preglednika i u zaglavlju `Referer`, a vrijedi danima. Kod se troši pri prvoj razmjeni.
 */
export const exchangeImpersonationCode = async (
  code: string
): Promise<{ refreshToken: string; accessToken: string; impersonatedBy: string | null }> => {
  const resp = await createApiCall(
    {
      path: "/v1/users/impersonate/exchange",
      method: "POST",
      body: { code },
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return {
    refreshToken: resp.data.refreshToken,
    accessToken: resp.data.accessToken,
    impersonatedBy: resp.data.impersonatedBy ?? null,
  };
};

/**
 * Prijava Google računom. `credential` je token koji Google preda pregledniku; poslužitelj ga
 * provjerava i vraća naše tokene, pa je nastavak isti kao kod prijave lozinkom.
 */
export const logInWithGoogle = async (
  credential: string
): Promise<{ refreshToken: string; accessToken: string }> => {
  const resp = await createApiCall(
    {
      path: "/v1/users/authenticate/google",
      method: "POST",
      body: { credential },
    },
    {
      getMessage: {
        success: () => "Uspješno ste se prijavili! Dobro došli!",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return {
    refreshToken: resp.data.refreshToken,
    accessToken: resp.data.accessToken,
  };
};

export const registerUser = async (
  formValues: RegistrationFormValues
): Promise<void> => {
  await createApiCall(
    {
      path: "/v1/users/register",
      method: "POST",
      body: transformToRequestRegister(formValues),
    },
    {
      getMessage: {
        success: () => "Uspješno ste se registrirali.",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};

export const requestPasswordChange = async (email: string): Promise<void> => {
  await createApiCall(
    {
      path: "/v1/auth/register/reset_password",
      method: "POST",
      body: { email },
    },
    {
      getMessage: {
        success: () => "An email has been sent to you.",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();
};

export const changePassword = async (formValues: {
  email: string;
  verificationCode: string;
  password: string;
}): Promise<void> => {
  await createApiCall(
    {
      path: "/v1/auth/register/reset_password_finalize",
      method: "POST",
      body: formValues,
    },
    {
      getMessage: {
        success: () => "You changed password successfully, please login.",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();
};

export const refreshAccessToken = async (
  accessToken: string,
  refreshToken: string
): Promise<{ refreshToken: string; accessToken: string } | null> => {
  try {
    const resp = await createApiCall(
      {
        path: "/v1/users/tokens/refresh",
        method: "POST",
        body: {
          accessToken,
          refreshToken,
        },
      },
      {
        getMessage: {
          success: () => "",
          error: () => "", // Don't show error message for token refresh
        },
      }
    )();

    if (resp && resp.data && resp.data.accessToken && resp.data.refreshToken) {
      return {
        refreshToken: resp.data.refreshToken,
        accessToken: resp.data.accessToken,
      };
    }

    return null;
  } catch (error) {
    // Silently fail - the interceptor will handle sign out
    console.warn("Token refresh failed:", error);
    return null;
  }
};
